import React, { useRef, useContext, useEffect, useLayoutEffect, useState, useCallback, useMemo } from 'react';
import gsap from 'gsap';
import Overlay from './Overlay';
import Interstitial from './Interstitial';

import { OverlayData } from '../data/Timing';
import { VideoContext } from '../context/VideoContext';
import { fadeInTime, fadeOutTime, nudgeTime } from '../constants/index';

// eslint-disable-next-line no-empty-pattern
const Overlays = React.forwardRef(({totalTime, debugRef}, ref) => {

  const { overlays, currentProgress, setCurrentOverlay } = useContext(VideoContext);
  const timeline = useRef();
  const containerRefs = useRef(OverlayData.map(() => React.createRef()));

  let currentIndex = null;

  const showHideOverlay = index => {

    if (index !== null && index > -1 && currentIndex !== index) {
      containerRefs.current[index].current.style.pointerEvents = "auto";
      containerRefs.current[index].current.style.zIndex = "1000";      
      currentIndex = index;
      setCurrentOverlay(`overlay_${index}`);
    } else if (currentIndex > -1) {
      if (index === null && currentIndex === null) return;
      containerRefs.current[currentIndex].current.style.pointerEvents = "none";
      containerRefs.current[currentIndex].current.style.zIndex = "1";
      currentIndex = null;
      setCurrentOverlay(null);
    }
  }

  // setup the timeline of the Overlays fading in and out
  useEffect(() => {

    if (totalTime < 0) return;
    
    timeline.current = gsap.timeline({ paused: true });
    
    OverlayData.forEach((overlay, index) => {
      // IN
      let timeIn = ((overlay.seconds - fadeInTime) < 0 ? 0 : (overlay.seconds - fadeInTime)).toFixed(3);
      containerRefs.current[index].current.querySelector(".overlay-container").dataset.timing = timeIn / totalTime;
      let animIn = gsap.to(containerRefs.current[index].current, {
        opacity: 1, duration: fadeInTime, ease: "none",
      });
      timeline.current.add(animIn, timeIn);
      
      // OUT
      let timeOut = (overlay.seconds + overlay.hold).toFixed(3);
      let animOut = gsap.to(containerRefs.current[index].current, {
        opacity: 0, duration: fadeOutTime, ease: "none",
      });      
      timeline.current.add(animOut, timeOut);

      // make a call to showHideOverlay at the overlay fade in time to track the current active div (so we can set pointer events / z index on it)
      //   right before and after this call we set the active div to null so we can track active div forward and backward in the timeline
      timeline.current.call(showHideOverlay, [null], overlay.seconds - nudgeTime);
      timeline.current.call(showHideOverlay, [index], overlay.seconds);
      timeline.current.call(showHideOverlay, [index], overlay.seconds + overlay.hold);
      timeline.current.call(showHideOverlay, [null], overlay.seconds + overlay.hold + nudgeTime);

    });

    // add a tween at the very end of the timeline so the length of this timeline matches the duration of the video in order to sync up the video and overlay timelines
    timeline.current.add(
      gsap.to(containerRefs.current[0].current, { opacity: 0, duration: 0 }),
      totalTime
    );

    timeline.current.call(showHideOverlay, [null], 0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalTime]);

  // sync the timeline to the current time of the video (which is controlled by ScrollTrigger in Video.js)
  useEffect(() => {
    try {
      timeline.current.progress(currentProgress);
    } catch (error) {
      console.log(`Overlays: error setting timeline.current.progress = ${error}`);      
    }

  }, [timeline, currentProgress]);

  // only render the overlays one time, useMemo is like useEffect but only runs once
  const renderOverlays = useMemo(() => {

    return OverlayData.map((overlay, index) => (
      
      <div 
        key={index}
        ref={containerRefs.current[index]}
        id={`overlay-${index}`}
        className={`fixed inset-0 pointer-events-none ${overlays.visible ? "block" : "hidden"} opacity-0`}
      >
        {overlay?.interstitial ? (
          <Interstitial
            position={overlay.position}
            textSize={overlay.size}
          >
            {overlay.content}
          </Interstitial>
        ) : (
          <Overlay
            position={overlay.position}
            size={overlay.size}
            deepdive={overlay.deepdive}
            deepdiveIndex={overlay?.deepdiveIndex}
            index={index}
            disclaimer={overlay.disclaimer}
          >
            {overlay.content}
          </Overlay>
        )}
        
      </div>
    ));
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  

  return (
    <div id="overlayContainer" ref={ref} className="w-screen h-screen opacity-100 z-[1000] fixed top-0 left-0">
      {renderOverlays}
    </div>
  );
});

export default Overlays;